// ZoteroImport.tsx
// Zotero import screen — Dissertation+ tier only.
// Accepts files from a Zotero export folder via drag-and-drop or folder picker.

import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import type { Theme } from '../types';
import ThemeToggle from './ThemeToggle';
import styles from '../styles/ZoteroImport.module.css';

interface ZoteroImportProps {
  onZipSelected: (files: File[]) => void;
  onBack: () => void;
  theme: Theme;
  onToggleTheme: () => void;
}

const STEPS = [
  'Select a collection or your entire library',
  'File → Export Library (or right-click collection → Export)',
  'Choose BibTeX or Zotero RDF format',
  'Check "Export Files" to include PDFs',
  'Save — Zotero creates a folder with your .bib/.rdf and a files/ subfolder',
  'Open the exported folder and select all files inside it, then drag them here or click to select',
];

export default function ZoteroImport({
  onZipSelected,
  onBack,
  theme,
  onToggleTheme,
}: ZoteroImportProps) {
  const [staged, setStaged] = useState<File[] | null>(null);
  const folderInputRef = React.useRef<HTMLInputElement>(null);

  const onDrop = useCallback((accepted: File[]) => {
    if (accepted.length > 0) setStaged(accepted);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
    noDrag: false,
  });

  const dropZoneClass = [
    styles.dropZone,
    isDragActive ? styles.dropZoneActive : '',
    staged && !isDragActive ? styles.dropZoneStaged : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={styles.page}>
      <div className={styles.container}>

        <button className={styles.btnBack} onClick={onBack}>← Back</button>

        <h1 className={styles.title}>Zotero Import</h1>
        <p className={styles.subtitle}>
          Evaluate your entire Zotero collection in one go — no hunting for files, no manual uploads.
          Just export from Zotero and select the folder contents here.
        </p>

        {/* Instructions */}
        <div className={styles.instructions}>
          <div className={styles.instructionsLabel}>How to import from Zotero</div>
          {STEPS.map((step, i) => (
            <div key={i} className={styles.step}>
              <span className={styles.stepNumber}>{i + 1}.</span>
              <span className={styles.stepText}>{step}</span>
            </div>
          ))}
        </div>

        {/* Hidden folder input for click-to-select */}
        <input
          ref={folderInputRef}
          type="file"
          // @ts-ignore
          webkitdirectory=""
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              setStaged(Array.from(e.target.files));
            }
          }}
        />

        {/* Drop zone */}
        <div
          {...getRootProps()}
          className={dropZoneClass}
          onClick={(e) => {
            e.stopPropagation();
            folderInputRef.current?.click();
          }}
        >
          <input {...getInputProps()} />
          <div className={styles.dropIcon}>{staged ? '✅' : '📦'}</div>
          <div className={styles.dropTitle}>
            {staged
              ? `${staged.length} files ready`
              : isDragActive
              ? 'Drop your Zotero files here'
              : 'Select your Zotero export files'}
          </div>
          {staged
            ? <div className={styles.dropCount}>{staged.length} files selected</div>
            : <div className={styles.dropSubtitle}>Select all files from your Zotero export folder</div>
          }
        </div>

        {staged && (
          <div className={styles.actions}>
            <button className={styles.btnClear} onClick={() => setStaged(null)}>
              Clear
            </button>
            <button
              className={styles.btnEvaluate}
              onClick={() => staged && onZipSelected(staged)}
            >
              Evaluate Zotero library →
            </button>
          </div>
        )}
      </div>

      <ThemeToggle theme={theme} onToggle={onToggleTheme} />
    </div>
  );
}
